import React, { useState, useContext, useRef, useEffect, useMemo } from "react";
import { withStyles } from "@mui/styles";
import { createFilterOptions } from "@mui/material/Autocomplete";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { blue } from "@mui/material/colors";

import StackManagementDialog from "components/widgets/stacks-management-widget/StackManagementDialog";
import WidgetConfigureDialog from "../WidgetConfigureDialog";
import ComboBox from "components/ComboBox";
import ISXUtils, { ISXContext } from "services/Utils";

const USE_STACK_CONFIG = true;

const StackDetails = (props) => {
  const [open, setOpen] = useState(false);

  const { selectedStack } = props;

  return (
    <>
      <Tooltip
        title="Click to view/modify"
        onClick={(e) => {
          setOpen(true);
        }}
      >
        <OpenInNewIcon
          fontSize="small"
          sx={{ color: blue[500], cursor: "pointer" }}
        />
      </Tooltip>
      {open && (
        <StackManagementDialog
          detailsOpen={open}
          closeDetails={() => setOpen(false)}
          selectedStack={selectedStack}
          readOnly={false}
        />
      )}
    </>
  );
};

const StackTableCell = withStyles((theme) => ({
  head: {
    paddingLeft: 12,
    paddingRight: 12,
  },
  body: {
    paddingLeft: 12,
    paddingRight: 12,
  },
}))(TableCell);

const StackRow = (props) => {
  const isxContext = useContext(ISXContext);

  const {
    idx,
    stack,
    stacks,
    stacksSortedByName,
    filterSelectedOptions,
  } = props;

  const setStack = (_, stack) => {
    props.selectStack(stack.guuid, idx);
  };

  return (
    <TableRow>
      <StackTableCell>
        {stacksSortedByName.length > 0 ? (
          <ComboBox
            value={stacks[stack] ?? null}
            options={stacksSortedByName}
            onChange={setStack}
            getOptionLabel={ISXUtils.getStackName}
            getOptionKey={(option) => option.guuid}
            filterOptions={filterSelectedOptions}
          />
        ) : (
          <Typography>
            No {isxContext.labels.deviceCapitalized} Available
          </Typography>
        )}
      </StackTableCell>
      <TableCell style={{ paddingLeft: 0, paddingRight: 0 }}>
        {USE_STACK_CONFIG && stack && <StackDetails selectedStack={stack} />}
      </TableCell>
      <StackTableCell>
        {props.deleteStack && (
          <IconButton onClick={() => props.deleteStack(stack)} size="large">
            <CancelIcon />
          </IconButton>
        )}
        {props.addStack && (
          <IconButton onClick={props.addStack} disabled={!stack} size="large">
            <AddCircleIcon />
          </IconButton>
        )}
      </StackTableCell>
    </TableRow>
  );
};

const WidgetStacksData = (props) => {
  const isxContext = useContext(ISXContext);

  const { maxStacks, stacksSelected } = props;

  const stacksSortedByName = useMemo(
    () =>
      props.stacks
        ? Object.values(props.stacks).sort((a, b) =>
            ISXUtils.strcasecmp(
              ISXUtils.getStackName(a),
              ISXUtils.getStackName(b)
            )
          )
        : [],
    [props.stacks]
  );

  const filterSelectedOptions = (stack) => (options, state) =>
    createFilterOptions()(options, state).filter(
      (option) => option.guuid === stack || !stacksSelected.has(option.guuid)
    );

  return (
    <Table>
      <TableHead>
        <TableRow>
          <StackTableCell>{isxContext.labels.deviceCapitalized}</StackTableCell>
          <StackTableCell />
          <StackTableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {[...stacksSelected].map((stack, idx) => {
          const addStack =
            idx === stacksSelected.size - 1 &&
            (!maxStacks || stacksSelected.size < maxStacks)
              ? props.addStack
              : null;
          const deleteStack = maxStacks !== 1 ? props.deleteStack : null;
          return (
            <StackRow
              {...props}
              idx={idx}
              key={stack}
              stack={stack}
              stacksSortedByName={stacksSortedByName}
              deleteStack={deleteStack}
              addStack={addStack}
              filterSelectedOptions={filterSelectedOptions(stack)}
            />
          );
        })}
      </TableBody>
    </Table>
  );
};

const WidgetStacksConfigureDialog = (props) => {
  const [stacksSelected, setStacksSelected] = useState();
  const [initialized, setInitialized] = useState(false);
  const dirty = useRef(false);
  const isxContext = useContext(ISXContext);

  const { widget, stacks = {}, maxStacks = Number.MAX_SAFE_INTEGER } = props;

  useEffect(() => {
    if (!initialized) {
      setStacksSelected(
        new Set(widget.stacks?.length > 0 ? widget.stacks : [null])
      );
      setInitialized(true);
    }
  }, [initialized, maxStacks, stacks, widget.stacks]);

  const selectStack = (guuid, idx) => {
    // need to convert to list to preserve order
    const newStacksSelected = [...stacksSelected];
    newStacksSelected[idx] = guuid;
    setStacksSelected(new Set(newStacksSelected));
    dirty.current = true;
  };

  const prepareStacksForWriteback = () => {
    return { stacks: [...stacksSelected] };
  };

  const areStacksValid = () => {
    return !stacksSelected.has(null);
  };

  const titleBuilder = (data) => {
    const stackIds = data.stacks;
    let title;
    if (stackIds.length === 1) {
      const stackid = stackIds[0];
      const stack = props.stacks[stackid] || {};
      const stackTitle =
        stack?.name || JSON.parse(stack.dev_session || "{}")["stack.label"];
      title = stackTitle;
    } else {
      title = props.widgetTitle || "Widget";
    }
    return title;
  };

  const addStack = () => {
    const newStacksSelected = new Set(stacksSelected);
    newStacksSelected.add(null);
    setStacksSelected(newStacksSelected);
  };

  const deleteStack = (guuid) => {
    const newStacksSelected = new Set(stacksSelected);
    newStacksSelected.delete(guuid);
    setStacksSelected(newStacksSelected);
    dirty.current = true;
  };

  return (
    initialized && (
      <WidgetConfigureDialog
        {...props}
        dataProperty="stacks"
        stacksSelected={stacksSelected}
        dataLabel={
          props.maxStacks !== 1
            ? isxContext.labels.devicePlural
            : isxContext.labels.device
        }
        prepareDataForWriteback={prepareStacksForWriteback}
        isDataValid={areStacksValid}
        titleBuilder={titleBuilder}
        dataDirty={dirty.current}
      >
        <WidgetStacksData
          {...props}
          stacksSelected={stacksSelected}
          selectStack={selectStack}
          addStack={addStack}
          deleteStack={deleteStack}
        />
      </WidgetConfigureDialog>
    )
  );
};

export default WidgetStacksConfigureDialog;
